import React, { useState } from 'react';
import './App.css';

const App = () => {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [auditResults, setAuditResults] = useState(null);
  const [error, setError] = useState('');
  const [showPopup, setShowPopup] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!url) {
      setError('Please enter a URL');
      return;
    }

    // Basic URL validation
    const urlPattern = /^https?:\/\/.+/;
    if (!urlPattern.test(url)) {
      setError('Please enter a valid URL starting with http:// or https://');
      return;
    }

    setIsLoading(true);
    setError('');
    setAuditResults(null);
    setShowPopup(true); // Show popup when audit starts

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to perform audit');
      }

      const data = await response.json();
      setAuditResults(data);
    } catch (err) {
      setError(err.message || 'An error occurred while performing the audit');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pass': return 'text-green-700 bg-green-50 border-green-200';
      case 'warning': return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'fail': return 'text-red-700 bg-red-50 border-red-200';
      default: return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return '✅';
      case 'warning': return '⚠️';
      case 'fail': return '❌';
      default: return '❓';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-green-500';
    if (score >= 70) return 'text-yellow-500';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 50) return 'text-orange-500';
    return 'text-red-600';
  };

  const getScoreGradient = (score) => {
    if (score >= 90) return 'from-green-400 to-green-600';
    if (score >= 80) return 'from-green-300 to-green-500';
    if (score >= 70) return 'from-yellow-300 to-yellow-500';
    if (score >= 60) return 'from-yellow-400 to-yellow-600';
    if (score >= 50) return 'from-orange-400 to-orange-600';
    return 'from-red-400 to-red-600';
  };

  const renderProgressBar = (score) => {
    return (
      <div className="relative w-full bg-gray-200 rounded-full h-4 mb-4">
        <div 
          className={`bg-gradient-to-r ${getScoreGradient(score)} h-4 rounded-full transition-all duration-1000 ease-out`}
          style={{ width: `${score}%` }}
        ></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-semibold text-white">{score}%</span>
        </div>
      </div>
    );
  };

  const renderExtractedData = (label, data, icon) => {
    if (!data) return null;
    
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <h4 className="flex items-center text-sm font-semibold text-blue-800 mb-2">
          <span className="mr-2">{icon}</span>
          {label}
        </h4>
        <div className="text-sm text-blue-700 bg-white rounded p-3 border border-blue-100">
          {typeof data === 'string' ? (
            <p className="break-all">{data}</p>
          ) : (
            <pre className="whitespace-pre-wrap">{JSON.stringify(data, null, 2)}</pre>
          )}
        </div>
      </div>
    );
  };

  const renderAuditItem = (title, data, description, icon) => (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 mb-6 border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="flex items-center text-lg font-bold text-gray-800">
          <span className="mr-3 text-2xl">{icon}</span>
          {title}
        </h3>
        <div className={`px-4 py-2 rounded-full text-sm font-bold border ${getStatusColor(data.status)}`}>
          {getStatusIcon(data.status)} {data.status.charAt(0).toUpperCase() + data.status.slice(1)}
        </div>
      </div>
      
      <p className="text-gray-600 mb-4 text-sm">{description}</p>
      
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <p className="text-sm font-semibold text-gray-700 mb-2">
          📊 Analysis Result: {data.message}
        </p>
      </div>

      {/* Display extracted data based on the type */}
      {title === "Page Title" && data.title && renderExtractedData("Current Page Title", data.title, "📝")}
      {title === "Meta Description" && data.description && renderExtractedData("Current Meta Description", data.description, "📄")}
      {title === "URL Structure" && data.url && renderExtractedData("Current URL", data.url, "🔗")}
      {title === "Headings Structure" && data.headings && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
          <h4 className="flex items-center text-sm font-semibold text-purple-800 mb-2">
            <span className="mr-2">🏷️</span>
            Found Headings ({data.total_headings} total)
          </h4>
          <div className="text-sm text-purple-700 bg-white rounded p-3 border border-purple-100 max-h-48 overflow-y-auto">
            {Object.entries(data.headings).map(([level, headings]) => (
              headings.length > 0 && (
                <div key={level} className="mb-2">
                  <strong className="text-purple-800">{level.toUpperCase()}:</strong>
                  <ul className="ml-4 mt-1">
                    {headings.slice(0, 5).map((heading, index) => (
                      <li key={index} className="text-purple-600 truncate">• {heading}</li>
                    ))}
                    {headings.length > 5 && (
                      <li className="text-purple-500 italic">... and {headings.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )
            ))}
          </div>
        </div>
      )}
      {title === "Content Quality" && data.word_count && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <h4 className="flex items-center text-sm font-semibold text-green-800 mb-2">
            <span className="mr-2">📊</span>
            Content Statistics
          </h4>
          <div className="text-sm text-green-700 bg-white rounded p-3 border border-green-100">
            <p><strong>Word Count:</strong> {data.word_count} words</p>
            <p><strong>Average Sentence Length:</strong> {data.avg_sentence_length} words</p>
          </div>
        </div>
      )}
      {title === "Internal Links" && data.links && data.links.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
          <h4 className="flex items-center text-sm font-semibold text-indigo-800 mb-2">
            <span className="mr-2">🔗</span>
            Found Internal Links ({data.count} total)
          </h4>
          <div className="text-sm text-indigo-700 bg-white rounded p-3 border border-indigo-100 max-h-48 overflow-y-auto">
            {data.links.map((link, index) => (
              <div key={index} className="mb-2 pb-2 border-b border-indigo-100 last:border-b-0">
                <p className="font-medium text-indigo-800 truncate">{link.anchor_text || 'No anchor text'}</p>
                <p className="text-indigo-600 text-xs truncate">{link.url}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {title === "External Links" && data.links && data.links.length > 0 && (
        <div className="bg-pink-50 border border-pink-200 rounded-lg p-4 mb-4">
          <h4 className="flex items-center text-sm font-semibold text-pink-800 mb-2">
            <span className="mr-2">🌐</span>
            Found External Links ({data.count} total)
          </h4>
          <div className="text-sm text-pink-700 bg-white rounded p-3 border border-pink-100 max-h-48 overflow-y-auto">
            {data.links.map((link, index) => (
              <div key={index} className="mb-2 pb-2 border-b border-pink-100 last:border-b-0">
                <p className="font-medium text-pink-800 truncate">{link.anchor_text || 'No anchor text'}</p>
                <p className="text-pink-600 text-xs truncate">{link.full_url}</p>
                {link.rel && link.rel.length > 0 && (
                  <p className="text-pink-500 text-xs">rel: {link.rel.join(', ')}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {title === "Schema Markup" && data.schemas_found && data.schemas_found.length > 0 && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-4">
          <h4 className="flex items-center text-sm font-semibold text-teal-800 mb-2">
            <span className="mr-2">🏗️</span>
            Found Schema Markup ({data.count} total)
          </h4>
          <div className="text-sm text-teal-700 bg-white rounded p-3 border border-teal-100">
            {data.schemas_found.map((schema, index) => (
              <div key={index} className="mb-2">
                <p><strong>Type:</strong> {schema.type} - <strong>Schema:</strong> {schema.schema_type}</p>
                <p className={`text-xs ${schema.valid ? 'text-green-600' : 'text-red-600'}`}>
                  {schema.valid ? '✅ Valid' : '❌ Invalid'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      {title === "Social Media Tags" && (data.og_tags || data.twitter_tags) && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-4">
          <h4 className="flex items-center text-sm font-semibold text-violet-800 mb-2">
            <span className="mr-2">📱</span>
            Social Media Tags Found
          </h4>
          <div className="text-sm text-violet-700 bg-white rounded p-3 border border-violet-100 max-h-48 overflow-y-auto">
            {data.og_tags && Object.keys(data.og_tags).length > 0 && (
              <div className="mb-3">
                <p className="font-semibold text-violet-800 mb-1">Open Graph Tags:</p>
                {Object.entries(data.og_tags).map(([key, value]) => (
                  <p key={key} className="text-xs mb-1">
                    <strong>{key}:</strong> {value}
                  </p>
                ))}
              </div>
            )}
            {data.twitter_tags && Object.keys(data.twitter_tags).length > 0 && (
              <div>
                <p className="font-semibold text-violet-800 mb-1">Twitter Tags:</p>
                {Object.entries(data.twitter_tags).map(([key, value]) => (
                  <p key={key} className="text-xs mb-1">
                    <strong>{key}:</strong> {value}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {title === "Page Speed" && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
          <h4 className="flex items-center text-sm font-semibold text-orange-800 mb-2">
            <span className="mr-2">⚡</span>
            Performance Metrics
          </h4>
          <div className="text-sm text-orange-700 bg-white rounded p-3 border border-orange-100">
            <p><strong>Load Time:</strong> {data.load_time}s</p>
            <p><strong>Content Size:</strong> {data.content_size_kb}KB</p>
          </div>
        </div>
      )}
      {title === "Mobile Usability" && data.viewport_tag && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-4">
          <h4 className="flex items-center text-sm font-semibold text-cyan-800 mb-2">
            <span className="mr-2">📱</span>
            Viewport Configuration
          </h4>
          <div className="text-sm text-cyan-700 bg-white rounded p-3 border border-cyan-100">
            <p><strong>Viewport Tag:</strong> {data.viewport_tag}</p>
          </div>
        </div>
      )}
      {title === "Canonical Tag" && data.canonical_url && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
          <h4 className="flex items-center text-sm font-semibold text-emerald-800 mb-2">
            <span className="mr-2">🔗</span>
            Canonical URL Found
          </h4>
          <div className="text-sm text-emerald-700 bg-white rounded p-3 border border-emerald-100">
            <p className="break-all">{data.canonical_url}</p>
          </div>
        </div>
      )}
      {title === "Language Tag" && data.language && (
        <div className="bg-lime-50 border border-lime-200 rounded-lg p-4 mb-4">
          <h4 className="flex items-center text-sm font-semibold text-lime-800 mb-2">
            <span className="mr-2">🌐</span>
            Language Setting
          </h4>
          <div className="text-sm text-lime-700 bg-white rounded p-3 border border-lime-100">
            <p><strong>Language:</strong> {data.language}</p>
          </div>
        </div>
      )}
      {title === "Security" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <h4 className="flex items-center text-sm font-semibold text-red-800 mb-2">
            <span className="mr-2">🔒</span>
            Security Status
          </h4>
          <div className="text-sm text-red-700 bg-white rounded p-3 border border-red-100">
            <p><strong>HTTPS Enabled:</strong> {data.https_enabled ? '✅ Yes' : '❌ No'}</p>
          </div>
        </div>
      )}
        
      {data.recommendations && data.recommendations.length > 0 && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="flex items-center text-sm font-semibold text-blue-800 mb-3">
            <span className="mr-2">💡</span>
            Recommendations for Improvement
          </h4>
          <ul className="text-sm text-blue-700 space-y-2">
            {data.recommendations.map((rec, index) => (
              <li key={index} className="flex items-start">
                <span className="text-blue-500 mr-2 mt-1">•</span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
        
      {data.issues && data.issues.length > 0 && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h4 className="flex items-center text-sm font-semibold text-red-800 mb-3">
            <span className="mr-2">⚠️</span>
            Issues Found
          </h4>
          <ul className="text-sm text-red-700 space-y-2">
            {data.issues.map((issue, index) => (
              <li key={index} className="flex items-start">
                <span className="text-red-500 mr-2 mt-1">•</span>
                <span>{issue}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Popup Banner */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 border border-gray-200">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                🔍 Free SEO Scan Results
              </h3>
              <p className="text-gray-600 mb-6 text-sm">
                This is the free version scan results. If you want a full comprehensive SEO audit and SEO optimization for your web app, contact us for professional services.
              </p>
              <div className="flex flex-col space-y-3">
                <a
                  href="https://holmena.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  🚀 Get Full SEO Audit - Visit Holmena.com
                </a>
                <button
                  onClick={() => setShowPopup(false)}
                  className="text-gray-500 hover:text-gray-700 py-2 px-4 rounded-xl border border-gray-300 hover:border-gray-400 transition-colors"
                >
                  Continue with Free Version
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
{/* Header */}
<div className="bg-white shadow-lg border-b border-gray-200">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Left: Logo */}
      <img
        src="https://i.ibb.co/C5cBLSF8/Logo-H-transparent.png"
        alt="Holmena Logo"
        className="w-48 h-auto object-contain"
      />
          
      {/* Right: Paragraph */}
      <p className="text-xl text-gray-600 max-w-xl text-right">
        Get a free comprehensive SEO analysis of your website with detailed insights and actionable recommendations
      </p>
      

      
    </div>
  </div>
</div>



      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* URL Input Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="url" className="block text-lg font-semibold text-gray-700 mb-3">
                🌐 Enter the URL you want to analyze:
              </label>
              <input
                type="url"
                id="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                className="w-full px-6 py-4 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                disabled={isLoading}
              />
            </div>
            
            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                <p className="text-red-700 font-medium flex items-center">
                  <span className="mr-2">❌</span>
                  {error}
                </p>
              </div>
            )}
            
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8 rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-4 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                  <span>🔍 Analyzing Your Website...</span>
                </div>
              ) : (
                <span>🚀 Start Free SEO Audit</span>
              )}
            </button>
          </form>
        </div>

        {/* Results */}
        {auditResults && (
          <div className="space-y-8">
            {/* Overall Score */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  🎯 Free SEO Audit Results
                </h2>
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <p className="text-blue-600 text-lg font-semibold break-all">
                    📊 Analyzed Website: {auditResults.url}
                  </p>
                </div>
                
                <div className="max-w-md mx-auto">
                  {renderProgressBar(auditResults.overall_score)}
                </div>
                
                <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-6 shadow-lg">
                  <span className={`text-4xl font-bold ${getScoreColor(auditResults.overall_score)}`}>
                    {auditResults.overall_score}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-700 mb-2">
                  Overall SEO Score (Free Version)
                </h3>
                <p className="text-gray-600">
                  {auditResults.overall_score >= 90 ? '🎉 Excellent!' : 
                   auditResults.overall_score >= 80 ? '👍 Very Good!' :
                   auditResults.overall_score >= 70 ? '👌 Good!' :
                   auditResults.overall_score >= 60 ? '⚠️ Needs Improvement' :
                   '🔧 Requires Attention'}
                </p>
              </div>
            </div>

            {/* Audit Items */}
            <div className="grid gap-8">
              {renderAuditItem(
                "Page Title",
                auditResults.title,
                "The page title should be descriptive, unique, and between 50-60 characters.",
                "📝"
              )}
              
              {renderAuditItem(
                "Meta Description",
                auditResults.meta_description,
                "The meta description should be compelling and between 150-160 characters.",
                "📄"
              )}
              
              {renderAuditItem(
                "URL Structure",
                auditResults.url_structure,
                "URLs should be clean, readable, and SEO-friendly.",
                "🔗"
              )}
              
              {renderAuditItem(
                "Headings Structure",
                auditResults.headings,
                "Proper heading hierarchy (H1-H6) helps search engines understand content structure.",
                "🏷️"
              )}
              
              {renderAuditItem(
                "Content Quality",
                auditResults.content_quality,
                "Content should be original, valuable, and well-structured.",
                "📊"
              )}
              
              {renderAuditItem(
                "Internal Links",
                auditResults.internal_links,
                "Internal links help with site navigation and SEO.",
                "🔗"
              )}
              
              {renderAuditItem(
                "External Links",
                auditResults.external_links,
                "External links should be relevant and use proper attributes.",
                "🌐"
              )}
              
              {renderAuditItem(
                "Image Optimization",
                auditResults.images,
                "Images should have descriptive alt text and be properly optimized.",
                "🖼️"
              )}
              
              {renderAuditItem(
                "Schema Markup",
                auditResults.schema_markup,
                "Structured data helps search engines understand your content.",
                "🏗️"
              )}
              
              {renderAuditItem(
                "Canonical Tag",
                auditResults.canonical_tag,
                "Canonical tags prevent duplicate content issues.",
                "🔗"
              )}
              
              {renderAuditItem(
                "Mobile Usability",
                auditResults.mobile_usability,
                "Your site should be mobile-friendly and responsive.",
                "📱"
              )}
              
              {renderAuditItem(
                "Page Speed",
                auditResults.page_speed,
                "Fast loading times improve user experience and SEO.",
                "⚡"
              )}
              
              {renderAuditItem(
                "Indexability",
                auditResults.indexability,
                "Your page should be indexable by search engines.",
                "🔍"
              )}
              
              {renderAuditItem(
                "Social Media Tags",
                auditResults.social_media_tags,
                "Open Graph and Twitter Card tags improve social sharing.",
                "📱"
              )}
              
              {renderAuditItem(
                "Language Tag",
                auditResults.language_tag,
                "The HTML lang attribute helps search engines understand content language.",
                "🌐"
              )}
              
              {renderAuditItem(
                "Security",
                auditResults.security,
                "HTTPS and security headers are important for SEO and user trust.",
                "🔒"
              )}
              
              {renderAuditItem(
                "Favicon",
                auditResults.favicon,
                "A favicon improves user experience and brand recognition.",
                "🌟"
              )}
            </div>
          </div>
        )}
      </div>
      
{/* Footer */}
<footer className="bg-white border-t border-gray-200 mt-auto">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
    <div className="text-center">
      <h3 className="text-lg font-semibold text-gray-800 mb-2">
        🔍 Holmena Free ON-Page SEO Scanner
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        Free SEO analysis tool - For comprehensive SEO services and optimization
      </p>
      <a
        href="https://holmena.com"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors"
      >
        Visit Holmena.com for Full SEO Services
        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </a>
    </div>
  </div>
</footer>

    </div>
  );
};

export default App;
